import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AlertContainerComponent } from './shared/components/alert-container/alert-container.component';
import { VersionCheckService } from './core/services/version-check.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, AlertContainerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'consent-app';
  private routerSubscription?: Subscription;
  private tawkLoaded = false;

  constructor(
    private versionCheckService: VersionCheckService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    console.log('AppComponent initialized');
    console.log(`📦 Current version: ${environment.version}`);

    // Start automatic version checking (every 5 minutes)
    // - showNotification: true = แสดง confirm dialog ก่อน reload
    // - autoReload: true = reload อัตโนมัติหลัง 5 วินาที ถ้าผู้ใช้ไม่กด Cancel
    if (environment.production) {
      this.versionCheckService.startVersionCheck(true, true);
    } else {
      console.log('🔧 Development mode: Version check disabled');
    }

    // Load Tawk.to only on customer pages (consent pages and home)
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      const isCustomerPage = this.isCustomerPage(event.url);

      if (isCustomerPage && !this.tawkLoaded) {
        this.loadTawkTo();
      } else if (!isCustomerPage && this.tawkLoaded) {
        this.removeTawkTo();
      }
    });

    // Check initial route
    const isCustomerPage = this.isCustomerPage(this.router.url);
    if (isCustomerPage) {
      this.loadTawkTo();
    }
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.removeTawkTo();
  }

  private isCustomerPage(url: string): boolean {
    // Check if it's home page (root or empty)
    const isHome = url === '/' || url === '' || url === '/index.html';
    // Check if it's consent page
    const isConsent = url.includes('/consent/');

    return isHome || isConsent;
  }

  private loadTawkTo() {
    if (this.tawkLoaded) return;

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.text = `
      var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();

      // Set Thai language
      Tawk_API.locale = 'th';

      // Optional: Customize widget text in Thai
      Tawk_API.customStyle = {
        visibility : {
          desktop : {
            position : 'br',
            xOffset : 20,
            yOffset : 20
          },
          mobile : {
            position : 'br',
            xOffset : 10,
            yOffset : 10
          }
        }
      };

      (function(){
        var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
        s1.async=true;
        s1.src='https://embed.tawk.to/68fb4777f8063f194f998201/1j8aoub49';
        s1.charset='UTF-8';
        s1.setAttribute('crossorigin','*');
        s1.id='tawk-script';
        s0.parentNode.insertBefore(s1,s0);
      })();
    `;
    this.renderer.appendChild(this.document.body, script);
    this.tawkLoaded = true;
    console.log('✅ Tawk.to loaded for customer page (Thai language)');
  }

  private removeTawkTo() {
    if (!this.tawkLoaded) return;

    // Remove Tawk widget
    const tawkWidget = this.document.getElementById('tawk-script');
    if (tawkWidget) {
      tawkWidget.remove();
    }

    // Remove all Tawk iframes if they exist
    const tawkIframes = this.document.querySelectorAll('[id^="tawk"]');
    tawkIframes.forEach((iframe) => {
      iframe.remove();
    });

    // Clean up Tawk global variables
    // Create stub objects instead of setting to undefined to prevent errors
    // when Tawk script tries to access properties
    if ((window as any).Tawk_API) {
      // Hide the widget if hideWidget method exists
      if (typeof (window as any).Tawk_API.hideWidget === 'function') {
        (window as any).Tawk_API.hideWidget();
      }
      // Replace with empty stub object to prevent "Cannot read properties of undefined" errors
      (window as any).Tawk_API = {
        onLoaded: function() {},
        onBeforeLoad: function() {},
        onLoad: function() {},
        showWidget: function() {},
        hideWidget: function() {},
        toggle: function() {},
        maximize: function() {},
        minimize: function() {},
        popup: function() {},
        setAttributes: function() {},
        addEvent: function() {},
        addTags: function() {},
        removeTags: function() {}
      };
    }
    if ((window as any).Tawk_LoadStart) {
      (window as any).Tawk_LoadStart = undefined;
    }

    this.tawkLoaded = false;
    console.log('❌ Tawk.to removed from admin page');
  }
}